import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  MapPin,
  QrCode,
  TrendingUp,
  ArrowRight,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { format, parseISO } from "date-fns";

export default function Dashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Members",
      value: stats?.total_members || 0,
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      title: "Active Members",
      value: stats?.active_members || 0,
      icon: CheckCircle2,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      title: "Locations",
      value: stats?.total_locations || 0,
      icon: MapPin,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      title: "Today's Check-ins",
      value: stats?.today_check_ins || 0,
      icon: QrCode,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    }
  ];

  const formatChartData = () => {
    if (!stats?.chart_data) return [];
    return stats.chart_data.map((item) => ({
      ...item,
      dateLabel: format(parseISO(item.date), "MMM d")
    }));
  };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Welcome back, {user?.name}
          </p>
        </div>
        <Button onClick={() => navigate("/check-in")} className="btn-press" data-testid="quick-checkin-btn">
          <QrCode className="mr-2 h-4 w-4" />
          Quick Check-in
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="stat-card card-hover animate-slide-up"
            style={{ animationDelay: `${index * 0.05}s` }}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                  <p className="stat-value mt-2">{stat.value.toLocaleString()}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2 border-0 shadow-sm" data-testid="checkins-chart">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-gray-400" />
              Check-ins (Last 7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formatChartData()}>
                  <defs>
                    <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis 
                    dataKey="dateLabel" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#2563eb"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCount)"
                    name="Check-ins"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Check-ins */}
        <Card className="border-0 shadow-sm" data-testid="recent-checkins">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-400" />
                Recent Check-ins
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/reports")}
                data-testid="view-all-checkins-btn"
              >
                View all
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {stats?.recent_check_ins?.length > 0 ? (
              stats.recent_check_ins.slice(0, 5).map((checkIn) => (
                <div
                  key={checkIn.id}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {checkIn.member_name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {checkIn.location_name}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                      {checkIn.code}
                    </code>
                    <p className="text-xs text-gray-400 mt-1">
                      {format(parseISO(checkIn.checked_in_at), "h:mm a")}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <QrCode className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No check-ins today</p>
                <Button 
                  variant="link" 
                  onClick={() => navigate("/check-in")}
                  className="mt-2"
                >
                  Perform a check-in
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm" data-testid="quick-actions">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 card-hover"
              onClick={() => navigate("/members")}
              data-testid="quick-action-members"
            >
              <Users className="h-5 w-5" />
              <span>View Members</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 card-hover"
              onClick={() => navigate("/check-in")}
              data-testid="quick-action-checkin"
            >
              <QrCode className="h-5 w-5" />
              <span>Check-in</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 card-hover"
              onClick={() => navigate("/reports")}
              data-testid="quick-action-reports"
            >
              <TrendingUp className="h-5 w-5" />
              <span>Reports</span>
            </Button>
            {(user?.role === "super_admin" || user?.role === "main_office" || user?.role === "location_admin") && (
              <Button 
                variant="outline" 
                className="h-auto py-4 flex-col gap-2 card-hover"
                onClick={() => navigate("/locations")}
                data-testid="quick-action-locations"
              >
                <MapPin className="h-5 w-5" />
                <span>Locations</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
